<!-- Copyright 2026, Common Good Learning Tools LLC -->
<template>
<div class="k-lesson-featured-asset elevation-2"
	:class="top_css_class"
	type="button"
	@click.stop="$emit('asset_clicked', asset_id)"
>
	<div class="k-lesson-featured-asset-thumbnail">
		<img v-if="thumbnail" :src="thumbnail" />
		<k-icon v-else :color="`${element_color} darken-3`">{{icon}}</k-icon>
	</div>
	<div class="k-lesson-featured-asset-title" v-html="title"></div>
	<div v-if="description" class="k-lesson-featured-asset-description" v-html="description"></div>

	<div class="k-lesson-featured-asset-open-btn" :class="`${element_color} darken-3`">
		<!-- Since we're already showing the description here, skip the quick look; directly show icons to open or add/choose the asset -->
		<k-tooltip bottom><template v-slot:activator="{on}"><k-btn v-on="on" icon color="#fff" 
			@click.stop="$emit('asset_clicked', asset_id)"
		><k-icon>fas fa-up-right-from-square</k-icon>
		</k-btn></template>Open {{asset.type_label(true)}}</k-tooltip>
		
		<k-tooltip bottom><template v-slot:activator="{on}"><k-btn v-on="on" icon color="#fff"
			v-if="show_add_to_gc_btn"
			@click.stop="$emit('add_to_gc', asset_id)"
		><img src="./../../images/google_classroom_logo.png" alt="" aria-hidden="true" style="height: 22px; border-radius: 3px;">
		</k-btn></template>Add to Google Classroom</k-tooltip>
		
		<k-tooltip bottom><template v-slot:activator="{on}"><k-btn v-on="on" icon color="#fff"
			v-if="lti_chooser"
			@click.stop="$emit('lti_chooser_choose', asset_id)"
		><k-icon>fas fa-circle-check</k-icon>
		</k-btn></template>Choose {{asset.type_label(true)}}</k-tooltip>
	</div>
</div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'

export default {
	props: {
		asset: { type: Object, required: true },
		lc_color: { type: String, required: true },
		// nreq: { type: String, required: false, default() { return ''} },
	},
	data() { return {
	}},
	computed: {
		...mapState(['site_config', 'my_lessons', 'my_resources']),
		...mapGetters(['lti_chooser', 'my_default_collection', 'signed_in']),

		is_resource() { return !empty(this.asset.resource_id) },
		asset_id() { return this.asset.resource_id || this.asset.lesson_id },

		item_is_copy_for_user() {
			// if user isn't signed in, they can't have a copy
			if (!this.signed_in) return false

			// for lessons and sparkls, look first in my_x, then in my_default_collection
			if (this.asset.type == 'lesson') {
				let o = this.my_lessons?.find(x=>x.lesson_template_id == this.asset.lesson_id)
				if (o) return true

				if (this.my_default_collection) {
					o = this.my_default_collection.units[0].lessons.find(x=>x.lesson_template_id == this.asset.lesson_id)
					if (o) return true
				}

				return false
			} else if (this.asset.type == 'sparkl') {
				let o = this.my_resources?.find(x=>x.resource_template_id == this.asset.resource_id)
				if (o) return true

				if (this.my_default_collection) {
					o = this.my_default_collection.units[1].resources.find(x=>x.resource_template_id == this.asset.resource_id)
					if (o) return true
				}

				return false
			} else {
				// for link resources and sparkl_banks, we don't actually make "deep copies" for users
				return false
			}
		},

		top_css_class() {
			let s = `${this.element_color} lighten-5`
			if (this.item_is_copy_for_user) {	// not editing
				s += ' k-lesson-featured-asset-imported-to-my-collections'
			}
			return s
		},

		show_add_to_gc_btn() {
			// when viewing the lti_chooser, we show the "choose" button instead of the add to gc btn
			if (this.lti_chooser) return false
			return this.site_config.enable_add_to_gc_in_kebab == 'yes'
		},

		element_color() {
			if (this.asset.type == 'sparkl') return 'deep-purple'
			if (this.asset.type == 'assessment') return 'pink'
			return this.lc_color
		},

		thumbnail() {
			if (false && this.asset.type == 'sparkl') {
				return 'https://sparkl-ed.com/images/sparkl-logo-fff-200.png'
			}
			return ''
		},

		icon() {
			return this.asset.icon()
		},

		title() {
			if (this.is_resource) {
				return this.asset.description
			} else {
				return this.asset.lesson_title
			}
		},

		description() {
			// return 'The family is visiting Arequipa, a city in Peru. Here they meet a new friend, Ana, and introduce themselves saying where each of them are from.'
			if (this.is_resource) {
				if (this.asset.long_description) {
					return U.marked_latex(this.asset.long_description)
				}
			} else {
				// TODO: something for lesson?
			}
			// default is no description
			return ''
		},
	},
	watch: {
	},
	created() {
	},
	mounted() {
	},
	methods: {
	}
}
</script>

<style lang="scss">
.k-lesson-featured-asset {
	// border:1px solid black;
	display:flex;
	margin-bottom:12px;
	min-height:68px;
	background-color:$v-pink-lighten-5;
	border-radius:8px;
	border:3px solid transparent;

	cursor:pointer;

	p {
		margin:0 0 12px 0!important;
	}
}

.v-application .k-lesson-featured-asset-imported-to-my-collections.k-lesson-featured-asset-imported-to-my-collections {
	border:3px solid rgba(0,0,0,0.6)!important;
}

.k-lesson-featured-asset-thumbnail {
	flex:0 0 68px;
	text-align:center;
	margin-right:4px;
	img {
		height:60px;
		margin:4px 0 4px 4px;
		border-radius:6px;
	}
	.v-icon {
		font-size:44px;
		line-height:64px;
	}
}

.k-lesson-featured-asset-title {
	flex:1 1 35%;
	padding:12px 8px;
	// background-color:#fff;
	font-size:16px;
	font-weight:bold;
}

.k-lesson-featured-asset-description {
	flex:0 1 65%;
	padding:12px 8px;
	font-size:14px;
	line-height:18px;
}

.k-lesson-featured-asset-open-btn {
	margin:-3px -3px -3px 8px;
	padding:4px 8px;
	align-self:stretch;
	display:flex;
	flex-direction:column;
	justify-content:center;
	border-radius:0 8px 8px 0;
	.v-icon {
		font-size:24px;
	}
}
</style>